import React, { ReactNode } from 'react';
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react';
import { componentTypes, MenuRadio, RefinementList } from '@white-nl/instantsearch-components';
import { Keyable } from '@white-nl/instantsearch-components/dist/types/components';
import {
  useInstantSearch,
  useMenu,
  useRefinementList,
  useStats,
} from 'react-instantsearch';
import { ChevronDown, Filter } from '../../../../components/ui/Icons';
import Sidebar from '../../../../components/Sidebar/Sidebar';

function Panel({
  name,
  children,
}: {
  name: string,
  children: ReactNode,
}) {
  return (
    <Disclosure
      key={name}
      as="div"
      className="mb-4 last:mb-0"
      defaultOpen
    >
      <DisclosureButton className="group/open-close w-full justify-between font-semibold text-lg flex items-center gap-x-2.5 text-left py-3 border-b border-light-grey-1 transition-colors hover:text-light-grey-3">
        {name}
        <ChevronDown className="block w-auto h-2.5 transition-all mr-2 group-data-[open]/open-close:rotate-180" />
      </DisclosureButton>
      <DisclosurePanel className="py-4 md:py-6">
        {children}
      </DisclosurePanel>
    </Disclosure>
  );
}

function MenuFilter({
  name,
  additionalData,
}: {
  name: string,
  additionalData: Keyable,
}) {
  const { results } = useInstantSearch();
  const menu = useMenu({
    attribute: name,
    sortBy: ['name:asc'],
    limit: 999,
  });

  const { items } = menu;

  if (items.length === 0) {
    return '';
  }

  return (
    <Panel name={name}>
      <MenuRadio
        results={results}
        menu={menu}
        menuSettings={{
          attribute: name,
          sortBy: ['name:asc'],
          limit: 999,
          classNames: {
            list: 'flex flex-wrap gap-y-2.5',
            item: 'w-full relative flex items-center min-h-7.5 cursor-pointer text-base leading-normal pl-10.5',
            label: 'underline underline-offset-2 decoration-transparent hover:decoration-light-grey-2 transition-all after:transition-all after:w-7.5 after:h-7.5 after:rounded-full after:border-2 after:border-light-grey-1.5 hover:after:border-dark-shade after:bg-white after:absolute after:top-2/4 after:-translate-y-2/4 after:left-0 before:absolute before:w-4.7 before:h-4.7 before:absolute before:top-2/4 before:-translate-y-2/4 before:left-1.5 before:bg-near-black before:z-10 before:rounded-full before:opacity-0 peer-checked:before:opacity-100',
            radio: 'peer absolute opacity-0 invisible',
            count: 'hidden',
          },
        }}
        additionalData={additionalData}
      />
    </Panel>
  );
}

function RefinementFilter({
  name,
  additionalData,
}: {
  name: string,
  additionalData: Keyable,
}) {
  const { results } = useInstantSearch();
  const refinement = useRefinementList({
    attribute: name,
    sortBy: ['name:asc'],
    limit: 999,
  });

  const { items } = refinement;

  if (items.length === 0) {
    return '';
  }

  return (
    <Panel name={name}>
      <RefinementList
        results={results}
        refinementList={refinement}
        refinementListSettings={{
          attribute: name,
          sortBy: ['name:asc'],
          limit: 999,
          classNames: {
            list: 'flex flex-col flex-wrap gap-y-2.5',
            label: 'group/label flex gap-x-4 items-center relative min-h-7.5 decoration-transparent transition-all cursor-pointer text-base leading-normal pl-10.5 after:transition-all after:w-7.5 after:h-7.5 after:rounded after:border-2 after:border-light-grey-1.5 hover:after:border-dark-shade after:bg-white after:flex after:justify-center after:items-center after:absolute after:top-2/4 after:-translate-y-2/4 after:left-0 has-[:checked]:after:content-check',
            labelText: 'group-hover/label:underline group-hover/label:decoration-light-grey-2 underline-offset-2',
            checkbox: 'absolute opacity-0 invisible',
            count: 'min-w-5.5 h-5.5 flex items-center justify-center rounded py0.5 px-1 bg-light-shade text-sm leading-normal hover:no-underline',
          },
        }}
        additionalData={additionalData}
      />
    </Panel>
  );
}

export default function FilterSidebar({
  additionalData,
}: componentTypes.CustomContainerProps) {
  const {
    productSpecTypes,
    catalog,
  } = additionalData;

  const { nbHits } = useStats();

  if (Object.keys(productSpecTypes).length === 0) {
    return '';
  }

  return (
    <Sidebar
      sidebarButtonContent={(
        <>
          {Craft.t(catalog, 'Filters')}
          <Filter />
        </>
      )}
      sidebarTitle={Craft.t(catalog, 'Filteren')}
      sidebarPanelContent={(
        <>
          {Object.keys(productSpecTypes).map((name: string) => {
            const dataType = productSpecTypes[name];
            if ([1, 2].includes(parseInt(dataType, 10))) {
              return <MenuFilter key={name} name={name} additionalData={additionalData} />;
            }
            return <RefinementFilter key={name} name={name} additionalData={additionalData} />;
          })}
        </>
      )}
      closeButtonText={Craft.t(catalog, 'Toon {qty} resultaten', {
        qty: nbHits,
      })}
    />
  );
}
